import type { GetUsersListItem } from "@birdwatch/client";
import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { cn } from "cn";
import { useMemo } from "react";
import Avatar from "@/components/ui/avatar/avatar";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { BwUserLine } from "@/components/ui/icons";
import { useGetUsersList } from "@/features/admin/users/_api";
import { getDefaultAccount } from "@/libs/account";

function UserOptionDisplay({
  user,
  showAccountName = false,
}: {
  user: Pick<
    GetUsersListItem,
    "avatarUrl" | "name" | "isActivated" | "accounts"
  >;
  showAccountName?: boolean;
}) {
  return (
    <div className="flex grow items-center gap-2">
      <Avatar src={user.avatarUrl || ""} name={user.name} size="sm" />
      <div className="flex flex-col justify-start">
        <p className="grow">
          {user.name}
          {!user.isActivated && (
            <>
              <span className="mx-1">-</span>
              <span className="italic text-text-dark-low">Inactive</span>
            </>
          )}
        </p>
        {showAccountName && (
          <p className="text-xs text-text-dark-low line-clamp-1">
            {getDefaultAccount(user.accounts as any[])?.name}
          </p>
        )}
      </div>
    </div>
  );
}

type UserSingleSelectProps = FieldWrapperInjectedProps & {
  className?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  params?: Record<string, any>;
  placeholder?: string;
  disabled?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "center" | "end";
  modal?: boolean;
  showAccountName?: boolean;
  "data-test"?: string;
};

const TAKE_ALL = 9999;

export default function UserSingleSelect({
  className,
  value,
  onChange,
  params,
  placeholder,
  disabled,
  defaultOpen,
  onOpenChange,
  align,
  modal = true,
  showAccountName = false,
  ...props
}: UserSingleSelectProps) {
  const { data: users = [], isLoading } = useGetUsersList()
    .filters({
      ...params,
    })
    .includes("accounts")
    .sort("name")
    .take(TAKE_ALL)
    .get();

  const options = useMemo<ComboboxOption[]>(
    () =>
      users.map((user) => ({
        value: user.id,
        label: user.name,
        avatarUrl: user.avatarUrl,
        trigger: <UserOptionDisplay user={user} />,
        detail: (
          <UserOptionDisplay user={user} showAccountName={showAccountName} />
        ),
      })),
    [users, showAccountName],
  );

  return (
    <Combobox
      data-test="user-select-trigger"
      {...props}
      value={value}
      onChange={onChange}
      options={options}
      isLoading={isLoading}
      placeholder={placeholder}
      searchPlaceholder="Search user..."
      emptyText="No user found."
      disabled={disabled}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      align={align}
      modal={modal}
      className={className}
      renderTrigger={(selected) =>
        selected ? (
          selected.trigger
        ) : (
          <div className="flex grow items-center gap-2">
            <div
              className={cn(
                /* sorted */
                "flex overflow-hidden",
                "shrink-0 items-center justify-center",
                "h-5 w-5",
                "rounded-full border border-dashed border-border-dark-low",
              )}
            >
              <BwUserLine className="text-text-dark-min size-4" />
            </div>
            <p className="text-sm text-text-dark-min line-clamp-1 grow">
              {placeholder}
            </p>
          </div>
        )
      }
    />
  );
}
